import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { adminApi } from '../api/adminApi';
import toast, { Toaster } from 'react-hot-toast';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';

import PageHeader from '../components/common/PageHeader';

const PlanForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isEditing = !!id;

  const initialFormData = {
    slug: 'basic',
    name: '',
    description: '',
    price: { monthly: 0, yearly: 0, currency: '₹' },
    limits: { maxCustomers: 50, maxTransactions: 100, maxProducts: 100, maxBusinesses: 1, maxStaff: 2 },
    features: {
      mailbox: false, aiAssist: false, whatsapp: false, exportReports: false,
      gstReports: false, invoiceCustomization: false, scheduledEmails: false,
      customBranding: false, apiAccess: false, multiBranch: false,
      staffManagement: false, customSmtp: false
    },
    isActive: true,
    isDefault: false
  };

  const [formData, setFormData] = useState(initialFormData);
  const [loading, setLoading] = useState(isEditing); // Only load if editing without state

  useEffect(() => {
    if (isEditing) {
      if (location.state?.plan) {
        populateForm(location.state.plan);
        setLoading(false);
      } else {
        // Fetch all plans and find the one we need if refreshed
        fetchPlanData();
      }
    }
  }, [isEditing, id]);

  const fetchPlanData = async () => {
    try {
      setLoading(true);
      const res = await adminApi.getPlans();
      const plan = res.data.data.find(p => p._id === id);
      if (plan) {
        populateForm(plan);
      } else {
        toast.error('Plan not found');
        navigate('/plans');
      }
    } catch (error) {
      toast.error('Failed to load plan');
    } finally {
      setLoading(false);
    }
  };

  const populateForm = (plan) => {
    setFormData({
      slug: plan.slug,
      name: plan.name,
      description: plan.description || '',
      price: { monthly: plan.price?.monthly || 0, yearly: plan.price?.yearly || 0, currency: plan.price?.currency || '₹' },
      limits: { ...initialFormData.limits, ...plan.limits },
      features: { ...initialFormData.features, ...plan.features },
      isActive: plan.isActive,
      isDefault: plan.isDefault
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      if (isEditing) {
        await adminApi.updatePlan(id, formData);
        toast.success('Plan updated successfully');
      } else {
        await adminApi.createPlan(formData);
        toast.success('Plan created successfully');
      }
      setTimeout(() => navigate('/plans'), 1000);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save plan');
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-transparent p-12 text-center text-gray-500 font-bold">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-transparent pb-12">
      <Toaster position="top-right" />
      
      <PageHeader 
        title={isEditing ? 'Edit Plan' : 'Create New Plan'}
        subtitle="Configure pricing tiers, features, and limits."
        backLink="/plans"
      />

      <main className="max-w-full mx-auto">
        <div className="bg-white rounded-[14px] shadow-[0_8px_30px_rgb(0,0,0,0.02)] overflow-hidden border-t-[6px] border-red-600">
          <form onSubmit={handleSave} className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
              
              {/* Basic Details */}
              <div className="space-y-5">
                <h3 className="text-xs font-extrabold text-gray-400 uppercase tracking-widest flex items-center">
                  <span className="w-6 h-6 rounded-full bg-red-100 text-red-600 flex items-center justify-center mr-2">1</span>
                  Basic Details
                </h3>
                
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1.5">Plan Name</label>
                  <input 
                    required type="text" className="w-full px-4 py-2.5 border border-gray-200 rounded-xl bg-gray-50 focus:bg-white focus:ring-2 focus:ring-red-500 font-bold text-gray-900 outline-none transition-all" 
                    value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} 
                    placeholder="e.g. Premium Pro"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1.5">Plan Slug</label>
                  <select 
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl bg-gray-50 font-bold text-gray-900 outline-none transition-all focus:ring-2 focus:ring-red-500"
                    value={formData.slug} onChange={e => setFormData({...formData, slug: e.target.value})}
                  >
                    <option value="free">Free</option>
                    <option value="basic">Basic</option>
                    <option value="premium">Premium</option>
                    <option value="enterprise">Enterprise</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1.5">Description</label>
                  <textarea 
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl bg-gray-50 focus:bg-white focus:ring-2 focus:ring-red-500 font-medium text-gray-900 outline-none transition-all" 
                    rows="3" value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})} 
                    placeholder="Brief description of who this plan is for..."
                  />
                </div>
                
                <div className="pt-2">
                  <h3 className="text-xs font-extrabold text-gray-400 uppercase tracking-widest flex items-center mb-4">
                    <span className="w-6 h-6 rounded-full bg-red-100 text-red-600 flex items-center justify-center mr-2">2</span>
                    Pricing (INR)
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-1.5">Monthly Price</label>
                      <div className="relative">
                        <span className="absolute inset-y-0 left-0 pl-4 flex items-center text-gray-500 font-bold">₹</span>
                        <input 
                          type="number" className="w-full pl-8 pr-4 py-2.5 border border-gray-200 rounded-xl bg-gray-50 font-bold text-gray-900 outline-none focus:ring-2 focus:ring-red-500 transition-all" 
                          value={formData.price.monthly} onChange={e => setFormData({...formData, price: {...formData.price, monthly: Number(e.target.value)}})} 
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-1.5">Yearly Price</label>
                      <div className="relative">
                        <span className="absolute inset-y-0 left-0 pl-4 flex items-center text-gray-500 font-bold">₹</span>
                        <input 
                          type="number" className="w-full pl-8 pr-4 py-2.5 border border-gray-200 rounded-xl bg-gray-50 font-bold text-gray-900 outline-none focus:ring-2 focus:ring-red-500 transition-all" 
                          value={formData.price.yearly} onChange={e => setFormData({...formData, price: {...formData.price, yearly: Number(e.target.value)}})} 
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Limits & Features */}
              <div className="space-y-5 bg-gray-50 p-6 rounded-2xl border border-gray-100">
                <h3 className="text-xs font-extrabold text-gray-400 uppercase tracking-widest flex items-center mb-1">
                  <span className="w-6 h-6 rounded-full bg-red-100 text-red-600 flex items-center justify-center mr-2">3</span>
                  Resource Limits <span className="lowercase ml-1 font-medium">(-1 for unlimited)</span>
                </h3>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1">Max Businesses</label>
                    <input type="number" className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-white font-bold text-sm outline-none focus:ring-2 focus:ring-red-500 transition-all" 
                      value={formData.limits.maxBusinesses} onChange={e => setFormData({...formData, limits: {...formData.limits, maxBusinesses: Number(e.target.value)}})} />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1">Max Staff</label>
                    <input type="number" className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-white font-bold text-sm outline-none focus:ring-2 focus:ring-red-500 transition-all" 
                      value={formData.limits.maxStaff} onChange={e => setFormData({...formData, limits: {...formData.limits, maxStaff: Number(e.target.value)}})} />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1">Max Customers</label>
                    <input type="number" className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-white font-bold text-sm outline-none focus:ring-2 focus:ring-red-500 transition-all" 
                      value={formData.limits.maxCustomers} onChange={e => setFormData({...formData, limits: {...formData.limits, maxCustomers: Number(e.target.value)}})} />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1">Max Products</label>
                    <input type="number" className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-white font-bold text-sm outline-none focus:ring-2 focus:ring-red-500 transition-all" 
                      value={formData.limits.maxProducts} onChange={e => setFormData({...formData, limits: {...formData.limits, maxProducts: Number(e.target.value)}})} />
                  </div>
                </div>

                <h3 className="text-xs font-extrabold text-gray-400 uppercase tracking-widest flex items-center pt-4 mb-2">
                  <span className="w-6 h-6 rounded-full bg-red-100 text-red-600 flex items-center justify-center mr-2">4</span>
                  Premium Features
                </h3>
                
                <div className="grid grid-cols-2 gap-y-4 gap-x-4 bg-white p-5 rounded-xl border border-gray-200">
                  {Object.keys(formData.features).map(feat => (
                    <label key={feat} className="flex items-center space-x-3 cursor-pointer group">
                      <div className="relative flex items-center">
                        <input 
                          type="checkbox" 
                          checked={formData.features[feat]} 
                          onChange={e => setFormData({...formData, features: {...formData.features, [feat]: e.target.checked}})}
                          className="peer sr-only"
                        />
                        <div className="w-5 h-5 border-2 border-gray-300 rounded peer-checked:bg-red-600 peer-checked:border-red-600 transition-all flex items-center justify-center">
                          <svg className="w-3.5 h-3.5 text-white opacity-0 peer-checked:opacity-100" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      </div>
                      <span className="text-xs font-bold text-gray-700 group-hover:text-red-600 transition-colors capitalize">
                        {feat.replace(/([A-Z])/g, ' $1').trim()}
                      </span>
                    </label>
                  ))}
                </div>
                
                <div className="pt-4 flex flex-col space-y-4">
                  <label className="flex items-center space-x-3 cursor-pointer p-3 bg-white rounded-xl border border-gray-200">
                    <div className="relative flex items-center">
                      <input 
                        type="checkbox" 
                        checked={formData.isActive} 
                        onChange={e => setFormData({...formData, isActive: e.target.checked})} 
                        className="peer sr-only" 
                      />
                      <div className={`w-11 h-6 bg-gray-200 rounded-full peer-checked:bg-red-600 transition-all after:content-[''] after:absolute after:top-1 after:left-1 after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-5 peer-checked:after:border-white`}></div>
                    </div>
                    <span className="text-sm font-extrabold text-gray-900">Plan is Active & Visible</span>
                  </label>
                  
                  <label className="flex items-center space-x-3 cursor-pointer p-3 bg-white rounded-xl border border-gray-200">
                    <div className="relative flex items-center">
                      <input 
                        type="checkbox" 
                        checked={formData.isDefault} 
                        onChange={e => setFormData({...formData, isDefault: e.target.checked})} 
                        className="peer sr-only" 
                      />
                      <div className={`w-11 h-6 bg-gray-200 rounded-full peer-checked:bg-blue-600 transition-all after:content-[''] after:absolute after:top-1 after:left-1 after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-5 peer-checked:after:border-white`}></div>
                    </div>
                    <div>
                      <span className="text-sm font-extrabold text-gray-900 block">Default Plan</span>
                      <span className="text-xs font-medium text-gray-500">Automatically assigned to new users</span>
                    </div>
                  </label>
                </div>
              </div>
              
            </div>
            
            <div className="pt-6 border-t border-gray-100 flex justify-end space-x-4">
              <button 
                type="button" 
                onClick={() => navigate('/plans')} 
                className="px-8 py-3 rounded-full text-sm font-bold text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button 
                type="submit" 
                className="px-8 py-3 rounded-full text-sm font-bold bg-red-600 text-white hover:bg-red-700 shadow-md transition-all transform hover:-translate-y-0.5 hover:shadow-lg flex items-center"
              >
                {isEditing ? 'Save Changes' : 'Publish Plan'}
                <ArrowLeftIcon className="h-4 w-4 ml-2 transform rotate-180" />
              </button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
};

export default PlanForm;
